import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';

/**
 * Prove a mobile number can actually receive an SMS before it is saved as an account's contact.
 *
 * Send → the server SMSes a 6-digit code and returns an id; Verify → the code is checked and the id
 * becomes spendable. The parent submits that id alongside the number, and the server refuses to save
 * a mobile whose id was not verified for exactly that number.
 *
 * Editing the number after verifying clears the verification — otherwise you could verify one number
 * and save a different one.
 */
export function MobileOtpVerify({ mobile, purpose, otpId, onVerified, disabled }: {
  mobile: string;
  purpose: 'MERCHANT_ADD' | 'MERCHANT_MOBILE_CHANGE';
  otpId: string | null;
  onVerified: (otpId: string | null) => void;
  disabled?: boolean;
}) {
  const [sentFor, setSentFor] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const verifiedFor = useRef<string | null>(null);

  const clean = String(mobile ?? '').replace(/\D+/g, '').slice(-10);
  const looksValid = /^[6-9]\d{9}$/.test(clean);
  const verified = !!otpId && verifiedFor.current === clean;

  // Changing the number invalidates whatever was verified or sent for the previous one.
  useEffect(() => {
    if (verifiedFor.current && verifiedFor.current !== clean) {
      verifiedFor.current = null;
      onVerified(null);
    }
    if (sentFor && sentFor !== clean) { setSentFor(null); setPendingId(null); setCode(''); setSecondsLeft(0); }
  }, [clean]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resend cooldown.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const send = useMutation({
    mutationFn: async () => (await api.post('/otp/send', { mobile: clean, purpose })).data,
    onSuccess: (r: any) => {
      setPendingId(String(r.otpId));
      setSentFor(clean);
      setCode('');
      setSecondsLeft(30);
      toast.success(`OTP sent to ${clean}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not send the OTP'),
  });

  const verify = useMutation({
    mutationFn: async () => (await api.post('/otp/verify', { otpId: pendingId, code: code.trim() })).data,
    onSuccess: (r: any) => {
      verifiedFor.current = clean;
      onVerified(String(r.otpId));
      toast.success('Mobile number verified');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not verify the OTP'),
  });

  if (verified) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-emerald-700 mt-1">
        <ShieldCheck size={14} /> Mobile {clean} verified
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-1.5">
      {sentFor === clean ? (
        <div className="flex items-center gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
            placeholder="6-digit OTP"
            inputMode="numeric"
            disabled={disabled}
            className="border rounded px-2 py-1 text-sm font-mono w-32"
          />
          <button type="button" onClick={() => verify.mutate()} disabled={disabled || verify.isPending || code.length !== 6}
            className="bg-emerald-600 text-white px-3 py-1 rounded text-xs disabled:opacity-50 flex items-center gap-1">
            {verify.isPending && <Loader2 size={12} className="animate-spin" />} Verify
          </button>
          <button type="button" onClick={() => send.mutate()} disabled={disabled || send.isPending || secondsLeft > 0}
            className="text-xs text-slate-600 underline disabled:opacity-50 disabled:no-underline">
            {secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend OTP'}
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => send.mutate()} disabled={disabled || !looksValid || send.isPending}
          title={!looksValid ? 'Enter a valid 10-digit mobile number first' : undefined}
          className="border border-emerald-500 text-emerald-700 px-3 py-1 rounded text-xs disabled:opacity-50 flex items-center gap-1">
          {send.isPending && <Loader2 size={12} className="animate-spin" />} Send OTP to verify
        </button>
      )}
      <p className="text-[11px] text-slate-500">This number must be verified by OTP before it can be saved.</p>
    </div>
  );
}
